import {
  Monitor,
  Play,
  Shield,
  Flag,
  Grid,
  Camera,
  Server,
  Smartphone,
  RotateCcw,
  Lock,
  MessageCircle,
  Gift,
  Diamond,
  CheckCircle,
  Trophy,
} from "lucide-react";
import { useState } from "react";
import apple from '../assets/logo/Apple_Pay.png'
import discover from '../assets/logo/Discover.png'
import cb from '../assets/logo/GIE_CB.png'
import Google from '../assets/logo/Google_Pay.png'
import mastercard from '../assets/logo/Mastercard.png'
import visa from '../assets/logo/Visa.png'
import { CONTACT_CONFIG } from "../config/contact";

function getDiscountPercent(original, current) {
  return Math.round(((original - current) / original) * 100);
}

function SeasonDiscountBadge({ originalPrice, currentPrice, variant = "default" }) {
  const pct = getDiscountPercent(originalPrice, currentPrice);
  const isPremium = variant === "premium";
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black mt-2 ${
      isPremium
        ? "bg-yellow-400 text-black"
        : "bg-gradient-to-r from-yellow-400 to-yellow-600 text-black"
    }`}>
      <Trophy className="w-3 h-3" />
      -{pct}% OFERTA TEMPORADA 2026/27
    </div>
  );
}

function SeasonFeature({ variant = "default" }) {
  const isPremium = variant === "premium";
  return (
    <div className={`flex items-start ${isPremium ? "gap-3" : "gap-2 sm:gap-3"}`}>
      <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 ${
        isPremium ? "text-yellow-300" : "text-yellow-500 group-hover:text-yellow-300 transition-colors duration-500"
      }`} />
      <span className={`${isPremium ? "text-white text-sm" : "text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500"}`}>
        <strong className={isPremium ? "text-yellow-300" : "text-yellow-600 group-hover:text-yellow-300"}>
          La Liga &amp; Champions League 2026/27
        </strong>{" "}
        — Todos los partidos en HD/4K sin cortes
      </span>
    </div>
  );
}

export default function Pricing() {

  const { whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const [isMultiScreen, setIsMultiScreen] = useState(false);

  const PRICING_DATA = {
    basic: {
      name: "PAQUETE BÁSICO",
      originalPrice: 39.0,
      currentPrice: 29.0,
      duration: "15 MESES",
      bonus: "12 MESES + 3 MESES GRATIS",
      link: "https://neonpixelworks.shop/index-es.html?id=1",
    },
    premium: {
      name: "PAQUETE PREMIUM",
      originalPrice: 49.0,
      currentPrice: 39.0,
      duration: "24 MESES",
      bonus: "2 AÑOS",
      link: "https://neonpixelworks.shop/index-es.html?id=2",
    },
    gold: {
      name: "PAQUETE ORO",
      originalPrice: 69.0,
      currentPrice: 59.0,
      duration: "18 MESES",
      bonus: "2 PANTALLAS",
      link: "https://neonpixelworks.shop/index-es.html?id=3",
    },
    multi_2_screens: {
      name: "PAQUETE DIAMANTE ",
      originalPrice: 79.0,
      currentPrice: 69.0,
      duration: "2 años",
      bonus: "2 PANTALLAS",
      link: "https://neonpixelworks.shop/index-es.html?id=4",
    },
    multi_3_screens: {
      name: "PAQUETE FAMILIAR",
      originalPrice: 89.0,
      currentPrice: 79.0,
      duration: "18 MESES",
      bonus: "3 PANTALLAS",
      link: "https://neonpixelworks.shop/index-es.html?id=5",
    },
    multi_4_screens: {
      name: "PLAN 4 PANTALLAS",
      originalPrice: 99.0,
      currentPrice: 89.0,
      duration: "18 MESES",
      bonus: "4 PANTALLAS",
      link: "https://neonpixelworks.shop/index-es.html?id=6",
    },
    lifetime: {
      name: "PAQUETE DE POR VIDA",
      originalPrice: 159.0,
      price: 129.0,
      features: [
        "Pago único",
        "2 pantallas",
        "Acceso completo al Plan PREMIUM",
        "3 MESES de Garantía de Reembolso"
      ],
      link: "https://neonpixelworks.shop/index-es.html?id=7"
    }
  };

  const standardPlans = [
    {
      id: "standard-basic",
      cardPosition: "basic",
      name: PRICING_DATA.basic.name,
      badge: PRICING_DATA.basic.bonus,
      originalPrice: PRICING_DATA.basic.originalPrice,
      currentPrice: PRICING_DATA.basic.currentPrice,
      durationLabel: `/${PRICING_DATA.basic.duration}`,
      fbValue: 29.0,
      checkoutPlan: "basic",
      redirectUrl: PRICING_DATA.basic.link,
    },
    {
      id: "standard-premium",
      cardPosition: "premium",
      name: PRICING_DATA.premium.name,
      badge: PRICING_DATA.premium.bonus,
      originalPrice: PRICING_DATA.premium.originalPrice,
      currentPrice: PRICING_DATA.premium.currentPrice,
      durationLabel: `/${PRICING_DATA.premium.duration}`,
      fbValue: 39.0,
      checkoutPlan: "premium",
      redirectUrl: PRICING_DATA.premium.link,
    },
    {
      id: "standard-gold",
      cardPosition: "gold",
      name: PRICING_DATA.gold.name,
      badge: PRICING_DATA.gold.bonus,
      originalPrice: PRICING_DATA.gold.originalPrice,
      currentPrice: PRICING_DATA.gold.currentPrice,
      durationLabel: `/${PRICING_DATA.gold.duration}`,
      fbValue: 59.0,
      checkoutPlan: "gold",
      redirectUrl: PRICING_DATA.gold.link,
    },
  ];

  const multiScreenPlans = [
    {
      id: "multi-2-screens",
      cardPosition: "basic",
      name: "PAQUETE DIAMANTE",
      badge: "2 PANTALLAS • 2 AÑOS",
      originalPrice: 79.0,
      currentPrice: 69.0,
      durationLabel: "2 años",
      fbValue: 69.0,
      checkoutPlan: "multi_2_screens",
      redirectUrl: PRICING_DATA.multi_2_screens.link,
    },
    {
      id: "multi-3-screens",
      cardPosition: "premium",
      name: "PAQUETE PLATINO",
      badge: "3 PANTALLAS • 18 MESES",
      originalPrice: 89.0,
      currentPrice: 79.0,
      durationLabel: "18 MESES",
      fbValue: 79.0,
      checkoutPlan: "multi_3_screens",
      redirectUrl: PRICING_DATA.multi_3_screens.link,
    },
    {
      id: "multi-4-screens",
      cardPosition: "gold",
      name: "PAQUETE REAL",
      badge: "4 PANTALLAS • 18 MESES",
      originalPrice: 99.0,
      currentPrice: 89.0,
      durationLabel: "18 MESES",
      fbValue: 89.0,
      checkoutPlan: "multi_4_screens",
      redirectUrl: PRICING_DATA.multi_4_screens.link,
    },
  ];

  const activePlans = isMultiScreen ? multiScreenPlans : standardPlans;


  const createCheckoutUrl = (plan, redirectUrl) => {
    return `/checkout?plan=${plan}&redirect=${redirectUrl}`;
  };
  const formatPrice = (price) => {
    return `€${price}`;
  };
  return (
    <>
      <section className="py-12 sm:py-16 bg-slate-50 animated-bg">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-auto font-black mb-3 sm:mb-4 max-w-3xl leading-snug text-center">
              Descubre Nuestros{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                Mejores Planes
              </span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Oferta especial Temporada 2026/27 — precios rebajados por tiempo limitado
            </p>
          </div>

          {/* Season Promo */}
          <div className="mb-8 sm:mb-10">
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#050B14] via-[#0b1a4f] to-[#090979] border border-yellow-400/30 shadow-xl p-4 sm:p-6">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background:
                    "radial-gradient(circle at 12% 8%, rgba(250,204,21,0.28), transparent 42%), radial-gradient(circle at 90% 15%, rgba(37,99,235,0.32), transparent 48%), radial-gradient(circle at 65% 100%, rgba(220,38,38,0.22), transparent 55%)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050B14]/90 via-[#050B14]/70 to-[#090979]/80"></div>
              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-400/20 flex items-center justify-center border border-yellow-400/40 flex-shrink-0">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-yellow-400 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">
                      La Liga &amp; Champions League · Temporada 2026/27
                    </p>
                    <h3 className="text-white text-lg sm:text-xl font-black">
                      Descuento Especial Nueva Temporada
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      Todos los planes incluyen La Liga y Champions League en HD/4K
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="bg-yellow-400 text-black px-3 py-1.5 rounded-full text-xs font-black">
                    Hasta -26%
                  </span>
                  <span className="bg-white/10 text-white border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                    Partidos en vivo + Repeticiones
                  </span>
                  <span className="bg-white/10 text-white border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                    Activación Inmediata
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/*IBO*/}
          <div className="mb-8 sm:mb-12">
            {/* Bannière Promotionnelle */}
            <div className="relative bg-gradient-to-r from-[#00d4ff] to-[#090979] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              {/* Éléments décoratifs de fond */}
              <div className="absolute left-0 top-0 w-12 sm:w-16 h-full bg-[#04003b] opacity-80 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>
              <div className="absolute left-3 sm:left-4 top-0 w-8 sm:w-12 h-full bg-[#030024] opacity-60 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>

              {/* Contenu principal */}
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                {/* Texte principal */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-white mb-2">
                    Activación Gratuita Para La App IBO Pro Player
                  </h3>
                  <p className="text-blue-100 text-base sm:text-lg font-semibold">
                    Oferta especial limitada
                  </p>
                </div>

                {/* Icône cadeau moderne */}
                <div className="flex-shrink-0">
                  <div className="relative group">
                    {/* Cercle de fond avec dégradé bleu */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#00d4ff] to-[#090979] rounded-full shadow-2xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300">
                      {/* Icône Gift avec couleurs compatibles */}
                      <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                    </div>

                    {/* Effet de brillance animé */}
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full opacity-90 animate-pulse flex items-center justify-center">
                      <Diamond className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#00d4ff]" />
                    </div>

                    {/* Particules flottantes */}
                    <div className="absolute -top-2 -left-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00d4ff] rounded-full opacity-60 animate-bounce"></div>
                    <div
                      className="absolute -bottom-1 -right-2 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full opacity-80 animate-bounce"
                      style={{ animationDelay: "0.5s" }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Effet de particules flottantes */}
              <div className="absolute top-3 sm:top-4 right-6 sm:right-8 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full opacity-60 animate-bounce"></div>
              <div
                className="absolute top-6 sm:top-8 right-10 sm:right-12 w-1 h-1 bg-white rounded-full opacity-40 animate-bounce"
                style={{ animationDelay: "0.5s" }}
              ></div>
              <div
                className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full opacity-50 animate-bounce"
                style={{ animationDelay: "1s" }}
              ></div>
            </div>
          </div>
          {/* Toggle Standard / Multi-Screen */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="inline-flex items-center rounded-full bg-white shadow-md p-1 border border-gray-200">
              <button
                type="button"
                onClick={() => setIsMultiScreen(false)}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 ${
                  !isMultiScreen
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                ESTÁNDAR
              </button>
              <button
                type="button"
                onClick={() => setIsMultiScreen(true)}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 ${
                  isMultiScreen
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                MULTI-PANTALLA
              </button>
            </div>
          </div>
          {/* Pricing Cards */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            id="pricing"
          >
            {activePlans.map((plan, index) => {
              if (plan.cardPosition === "basic" && index === 0) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-clean group pricing-toggle-card"
                    id="premium-basico"
                  >
                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-gray-900 mb-3 sm:mb-4 group-hover:text-white transition-colors duration-500">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-bold mb-4 sm:mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-4 sm:mb-6">
                        {plan.originalPrice && (
                          <span className="text-gray-400 line-through text-lg group-hover:text-blue-200 transition-colors duration-500">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-red-600 group-hover:text-white transition-colors duration-500">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-gray-500 text-sm group-hover:text-blue-200 transition-colors duration-500 mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                          />
                        )}
                      </div>
                    </div>
                    {/* Bouton IBO Gratuit */}
                    <div className="mb-4 sm:mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-[#090979] font-bold py-2 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border-2 border-blue-300 group-hover:from-blue-200 group-hover:to-blue-300 group-hover:border-blue-400 text-sm sm:text-base">
                        <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      <SeasonFeature />
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Monitor className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          +79.000 Canales, +249.000 Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mt-1 flex-shrink-0 group-hover:text-orange-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Amazon Prime, Videoland, Viaplay, NPO Start Plus, Discovery+, HBO Max, Netflix, Apple+, Disney+...
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-1 flex-shrink-0 group-hover:text-green-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Ver LaLiga en vivo en DAZN, Movistar, Bein sports...
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Flag className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500 mt-1 flex-shrink-0 group-hover:text-pink-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Elección ilimitada de Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Grid className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mt-1 flex-shrink-0 group-hover:text-purple-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Calidad SD / HD / FULL HD / 4K
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Catch-up y EPG
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Server className="w-4 h-4 sm:w-5 sm:h-5 text-blue-800 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Servidores Rápidos y Estables
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-1 flex-shrink-0 group-hover:text-green-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Compatible con todos los dispositivos
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mt-1 flex-shrink-0 group-hover:text-orange-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Contenido actualizado regularmente
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mt-1 flex-shrink-0 group-hover:text-purple-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          15 Días de Garantía de Reembolso
                        </span>
                      </div>
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-lg group-hover:shadow-2xl text-sm sm:text-base min-h-[48px]"
                      onClick={() => {
                        if(window.fbq) {
                          window.fbq("track", "AddToCart");
                        }
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      ORDENAR AHORA
                    </button>

                    {/* Paiement Sécurisé Garanti */}
                    <div className="mt-6 sm:mt-8">
                      <div className="relative flex items-center justify-center">
                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-gray-400 transition-colors duration-500"></div>
                        <div className="px-2">
                          <p className="text-xs sm:text-sm text-gray-600 group-hover:text-white font-semibold whitespace-nowrap">
                            Pago Seguro Garantizado
                          </p>
                        </div>
                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-white transition-colors duration-500"></div>
                      </div>
                      <div className="mt-2 sm:mt-4">
                        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
                          <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (plan.cardPosition === "premium" && index === 1) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-premium pricing-toggle-card"
                  >
                    {/* Floating animated elements */}
                    <div
                      className="diamond"
                      style={{ top: "10%", left: "5%", animationDelay: "0s" }}
                    ></div>
                    <div
                      className="diamond"
                      style={{ top: "20%", right: "10%", animationDelay: "2s" }}
                    ></div>
                    <div
                      className="diamond"
                      style={{ bottom: "30%", left: "8%", animationDelay: "4s" }}
                    ></div>
                    <div
                      className="star"
                      style={{ top: "15%", right: "15%", animationDelay: "1s" }}
                    ></div>
                    <div
                      className="star"
                      style={{ bottom: "20%", right: "5%", animationDelay: "3s" }}
                    ></div>
                    <div
                      className="star"
                      style={{ top: "60%", left: "3%", animationDelay: "5s" }}
                    ></div>

                    {/* Popular Badge */}
                    <div className="badge bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-semibold">
                      EL MÁS POPULAR
                    </div>

                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-white mb-4">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-6">
                        {plan.originalPrice && (
                          <span className="text-blue-200 line-through text-lg">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-white">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-blue-200 text-sm mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                            variant="premium"
                          />
                        )}
                      </div>
                    </div>
                    {/* Bouton IBO Gratuit */}
                    <div className="mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-200 to-blue-300 hover:from-blue-300 hover:to-blue-400 text-[#090979] font-bold py-2 px-6 rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-3 border-2 border-blue-400">
                        <Gift className="w-5 h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      <SeasonFeature variant="premium" />
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <Lock className="w-5 h-5 text-pink-300 mt-1 flex-shrink-0" />
                          <div className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            18
                          </div>
                        </div>
                        <span className="text-white text-sm">
                          Canales + Películas para Adultos (con contraseña) 🔞
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Monitor className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          +79.000 Canales, +249.000 Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Play className="w-5 h-5 text-orange-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Amazon Prime, Videoland, Viaplay, NPO Start Plus, Discovery+, HBO Max, Netflix, Apple+, Disney+...
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-green-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Ver LaLiga en vivo en DAZN, Movistar, Bein sports...
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Flag className="w-5 h-5 text-pink-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Elección ilimitada de Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Grid className="w-5 h-5 text-purple-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Calidad SD / HD / FULL HD / 4K
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Camera className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">Catch-up et EPG</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Server className="w-5 h-5 text-blue-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Servidores Rápidos y Estables
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Smartphone className="w-5 h-5 text-green-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Compatible con todos los dispositivos
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-orange-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          Contenido actualizado regularmente
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-purple-300 mt-1 flex-shrink-0" />
                        <span className="text-white text-sm">
                          15 Días de Garantía de Reembolso
                        </span>
                      </div>
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]  hover:brightness-110 hover:scale-105 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 hover:shadow-lg shadow-2xl"
                      onClick={() => {
                        if(window.fbq) {
                          window.fbq("track", "AddToCart");
                        }
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      ORDENAR AHORA
                    </button>

                    {/* Paiement Sécurisé Garanti */}
                    <div className="mt-6 sm:mt-8">
                      <div className="relative flex items-center justify-center">
                        <div className="flex-1 h-px bg-blue-200"></div>
                        <div className="px-2">
                          <p className="text-xs sm:text-sm text-white font-semibold whitespace-nowrap">
                            Pago Seguro Garantizado
                          </p>
                        </div>
                        <div className="flex-1 h-px bg-blue-200"></div>
                      </div>
                      <div className="mt-2 sm:mt-4">
                        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
                          <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (plan.cardPosition === "gold" && index === 2) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-clean group pricing-toggle-card"
                  >
                    {/* Best Price Badge */}
                    <div className="badge bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-semibold">
                      MEJOR PRECIO
                    </div>

                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-white transition-colors duration-500">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-6">
                        {plan.originalPrice && (
                          <span className="text-gray-400 line-through text-lg group-hover:text-blue-200 transition-colors duration-500">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-red-600 group-hover:text-white transition-colors duration-500">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-gray-500 text-sm group-hover:text-blue-200 transition-colors duration-500 mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                          />
                        )}
                      </div>
                    </div>
                    {/* Bouton IBO Gratuit */}
                    <div className="mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-[#090979] font-bold py-2 px-6 rounded-xl transition-all duration-500 hover:shadow-lg flex items-center justify-center gap-3 border-2 border-blue-300 group-hover:from-blue-200 group-hover:to-blue-300 group-hover:border-blue-400">
                        <Gift className="w-5 h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      <SeasonFeature />
                      <div className="flex items-start justify-center gap-3">
                        <div className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="group-hover:text-blue-300"
                            width="20px"
                            height="20px"
                            viewBox="0 0 24 24"
                          >
                            <path fill="#64B5F6" d="M10 16h4v0h-4z">
                              <animate
                                fill="freeze"
                                attributeName="d"
                                begin="0.6s"
                                dur="0.2s"
                                values="M10 16h4v0h-4z;M10 16h4v6h-4z"
                              ></animate>
                            </path>
                            <g
                              fill="none"
                              stroke="#64B5F6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            >
                              <path
                                strokeDasharray={72}
                                strokeDashoffset={72}
                                d="M12 17h-10v-14h20v14Z"
                              >
                                <animate
                                  fill="freeze"
                                  attributeName="stroke-dashoffset"
                                  dur="0.6s"
                                  values="72;0"
                                ></animate>
                              </path>
                              <path
                                strokeDasharray={4}
                                strokeDashoffset={4}
                                d="M12 21h3M12 21h-3"
                              >
                                <animate
                                  fill="freeze"
                                  attributeName="stroke-dashoffset"
                                  begin="0.8s"
                                  dur="0.2s"
                                  values="4;0"
                                ></animate>
                              </path>
                            </g>
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20px"
                            height="20px"
                            viewBox="0 0 24 24"
                          >
                            <path fill="#64B5F6" d="M10 16h4v0h-4z">
                              <animate
                                fill="freeze"
                                attributeName="d"
                                begin="0.6s"
                                dur="0.2s"
                                values="M10 16h4v0h-4z;M10 16h4v6h-4z"
                              ></animate>
                            </path>
                            <g
                              fill="none"
                              stroke="#64B5F6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            >
                              <path
                                strokeDasharray={72}
                                strokeDashoffset={72}
                                d="M12 17h-10v-14h20v14Z"
                              >
                                <animate
                                  fill="freeze"
                                  attributeName="stroke-dashoffset"
                                  dur="0.6s"
                                  values="72;0"
                                ></animate>
                              </path>
                              <path
                                strokeDasharray={4}
                                strokeDashoffset={4}
                                d="M12 21h3M12 21h-3"
                              >
                                <animate
                                  fill="freeze"
                                  attributeName="stroke-dashoffset"
                                  begin="0.8s"
                                  dur="0.2s"
                                  values="4;0"
                                ></animate>
                              </path>
                            </g>
                          </svg>
                        </div>
                        <span className="text-gray-700 text-sm group-hover:text-white transition-colors duration-500">
                          Dos pantallas activas
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <Lock className="w-5 h-5 text-pink-500 mt-1 flex-shrink-0 group-hover:text-pink-300 transition-colors duration-500" />
                          <div className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            18
                          </div>
                        </div>
                        <span className="text-gray-700 text-sm group-hover:text-white transition-colors duration-500">
                          Canales + Películas para Adultos (con contraseña) 🔞
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Monitor className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-sm group-hover:text-white transition-colors duration-500">
                          +79.000 Canales, +249.000 Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mt-1 flex-shrink-0 group-hover:text-orange-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Amazon Prime, Videoland, Viaplay, NPO Start Plus, Discovery+, HBO Max, Netflix, Apple+, Disney+...
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-1 flex-shrink-0 group-hover:text-green-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Ver LaLiga en vivo en DAZN, Movistar, Bein sports...
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Flag className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500 mt-1 flex-shrink-0 group-hover:text-pink-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Elección ilimitada de Películas y Series
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Grid className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mt-1 flex-shrink-0 group-hover:text-purple-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Calidad SD / HD / FULL HD / 4K
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Catch-up y EPG
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Server className="w-4 h-4 sm:w-5 sm:h-5 text-blue-800 mt-1 flex-shrink-0 group-hover:text-blue-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Servidores Rápidos y Estables
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-1 flex-shrink-0 group-hover:text-green-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Compatible con todos los dispositivos
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mt-1 flex-shrink-0 group-hover:text-orange-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          Contenido actualizado regularmente
                        </span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mt-1 flex-shrink-0 group-hover:text-purple-300 transition-colors duration-500" />
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500">
                          15 Días de Garantía de Reembolso
                        </span>
                      </div>
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-lg group-hover:shadow-2xl text-sm sm:text-base min-h-[48px]"
                      onClick={() => {
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      ORDENAR AHORA
                    </button>

                    {/* Paiement Sécurisé Garanti */}
                    <div className="mt-6 sm:mt-8">
                      <div className="relative flex items-center justify-center">
                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-gray-400 transition-colors duration-500"></div>
                        <div className="px-2">
                          <p className="text-xs sm:text-sm text-gray-600 group-hover:text-white font-semibold whitespace-nowrap">
                            Pago Seguro Garantizado
                          </p>
                        </div>
                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-white transition-colors duration-500"></div>
                      </div>


                      <div className="mt-2 sm:mt-4">
                        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
                          <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                          <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>

          {/* LIFETIME PLAN */}
          <div className="flex justify-center mt-6 sm:mt-8 lg:mt-12">
            <div className="w-full lg:w-1/3 lg:max-w-md">
              <div className="relative bg-gradient-to-b from-[#04003b] to-[#090979] rounded-2xl p-1 shadow-2xl overflow-hidden group transition-all duration-500">
                {/* Animated Border Gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#00d4ff] via-[#090979] to-[#00d4ff] opacity-50 group-hover:opacity-100 transition-opacity duration-500 animate-spin-slow"></div>

                <div className="relative bg-[#04003b] rounded-xl p-8 h-full flex flex-col items-center text-center">
                  {/* Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                      OFERTA TEMPORADA 2026/27
                    </span>
                  </div>

                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#090979] mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <Diamond className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
                      {PRICING_DATA.lifetime.name}
                    </h3>
                    <div className="flex flex-col items-center gap-1">
                      {PRICING_DATA.lifetime.originalPrice && (
                        <span className="text-blue-300 line-through text-lg">
                          {formatPrice(PRICING_DATA.lifetime.originalPrice)}
                        </span>
                      )}
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl sm:text-5xl font-black text-white">
                          {PRICING_DATA.lifetime.price}€
                        </span>
                        <span className="text-blue-200 font-medium">/ único</span>
                      </div>
                      {PRICING_DATA.lifetime.originalPrice && (
                        <SeasonDiscountBadge
                          originalPrice={PRICING_DATA.lifetime.originalPrice}
                          currentPrice={PRICING_DATA.lifetime.price}
                          variant="premium"
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 mb-8 w-full text-left">
                    <div className="flex items-center gap-3 bg-yellow-400/10 p-3 rounded-lg border border-yellow-400/30 hover:bg-yellow-400/15 transition-colors">
                      <div className="p-1.5 bg-yellow-400/20 rounded-full">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                      </div>
                      <span className="text-white font-medium">
                        La Liga &amp; Champions League 2026/27 incluido en HD/4K
                      </span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-green-500/20 rounded-full">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-white font-medium">Pago único (Sin suscripción)</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-blue-500/20 rounded-full">
                        <Monitor className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-white font-medium">2 Pantallas simultáneas</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-purple-500/20 rounded-full">
                        <Gift className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-white font-medium">Acceso completo al Plan PREMIUM</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-orange-500/20 rounded-full">
                        <Shield className="w-5 h-5 text-orange-400" />
                      </div>
                      <span className="text-white font-medium">30 Días de Garantía de Reembolso</span>
                    </div>
                  </div>

                  <button
                    className="w-full bg-gradient-to-r from-[#00d4ff] to-[#090979] hover:from-[#090979] hover:to-[#00d4ff] text-white font-black py-4 px-8 rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                    onClick={() => {
                      window.open(
                        createCheckoutUrl('lifetime', PRICING_DATA.lifetime.link),
                        "_blank"
                      )
                    }}
                  >
                    <Gift className="w-5 h-5" />
                    ORDENAR AHORA
                  </button>

                  <p className="mt-4 text-xs text-blue-200 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Pago 100% Seguro
                  </p>
                  {/* Paiement Sécurisé Garanti */}
                  <div className="mt-2 sm:mt-4">
                    <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
                      <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 max-w-4xl mx-auto ">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center md:text-left">
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    <span className="text-[#00d4ff] font-semibold">
                      Si desea una suscripción personalizada
                    </span>
                    <span className="text-[#090979] font-semibold">
                      {" "}
                      (país, idioma, pantalla),
                    </span>
                    <br />
                    <span className="text-[#090979] font-semibold">
                      contáctenos vía WhatsApp o correo electrónico.
                    </span>
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 sm:gap-3 bg-[#25d366] hover:bg-[#128C7E] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base min-h-[44px]"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}